import { BaseRes } from 'types/base';
import {
  PmsProduct,
  ProductAttributeCode,
  ProductCategory,
  ProductCreateInfo,
  ProductUpdateInfo,
  ProductCateType,
  ProductCateListPayload,
  ProductCateUpdateNavStatusPayload,
  ProductCateUpdateShowStatusPayload,
  ProductUpdateInfoNewType,
  ProductPassportUrlItem,
} from 'types/pms';
import request, { ginzaxiaomaApiRequest } from '.';
import {
  PMS_PRODUCT_STOCK_STATUS,
  PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
  PRODUCT_STOCK_TYPE_OPTION_LIST,
} from 'commons/options';
import i18n from 'i18n';
import * as XLSX from 'xlsx';
import { store } from 'store';
import { z } from 'zod';
import { PRODUCT_SOURCE_TYPE_MAP } from 'constant/pms-product';
import { ProductTaxRegime } from 'constants/france-tax';

export const getProductCategoryTree = () =>
  request.get<BaseRes<ProductCategory[]>>(
    '/pms/productCategory/list/withChildren',
  );

export const getProductAttributeCodeList = () =>
  request.get<
    BaseRes<
      {
        name: string;
        itemList: ProductAttributeCode[];
      }[]
    >
  >('/pms/productAttributeCode/list');

type GetProductListDto = {
  pageSize: number;
  pageNum: number;
  productCategoryId?: number;
  keyword?: string;
  skuType?: number;
  stockPlace?: string;
  isFilterPromotion?: boolean;
  publishStatus?: number;
  transformPriceToJpyFlag?: number;
  productSn?: string;
  supportCrypto?: boolean;
  start?: string;
  end?: string;
  stockStatuses?: string[];
  /** 库存地多选；与 stockPlace 同时存在时以后端约定为准（stockPlaces 优先） */
  stockPlaces?: string[];
  stockPlaceList?: string[];
  rankList?: string[];
  sourceTypeList?: number[];
  oldProductSn?: string;
  soldStart?: string;
  soldEnd?: string;
  isHavePic?: boolean;
  brandNameList?: string[];
  haveOrderId?: boolean;
  entryStart?: string;
  entryEnd?: string;
};

export const getProductList = (data: GetProductListDto) =>
  request.post<
    BaseRes<{
      pageNum: number;
      pageSize: number;
      total: number;
      list: PmsProduct[];
    }>
  >('/pms/product/list', data, {
    params: {
      pageNum: data.pageNum,
      pageSize: data.pageSize,
    },
  });

export type PmsProductEs = Pick<
  PmsProduct,
  | 'id'
  | 'name'
  | 'productSn'
  | 'stockStatus'
  | 'price'
  | 'currency'
  | 'stock'
  | 'publishStatus'
  | 'stockPlace'
  | 'description'
  | 'recycleOrderCode'
  | 'recycleOrderId'
  | 'promotionType'
  | 'costPrice'
  | 'originalPrice'
  | 'brandName'
  | 'sourceType'
  | 'soldTime'
  | 'orderId'
  | 'productCategoryId'
  | 'referencePrice'
  | 'rank'
  | 'createdTime'
  | 'entryTime'
  | 'pic'
> & {
  material?: string[];
  hardware?: string[];
  colors?: string;
  stamp?: string;
  priceJpy: number;
  materialStr: string;
  name_ja?: string;
  name_en?: string;
  fullStamp?: string;
  skuType: number;
  memberId?: number;
  email?: string;
  remark?: string;
};

/** 新版商品列表 */
export const getProductListNew = (data: GetProductListDto) => {
  return ginzaxiaomaApiRequest.post<{
    total: number;
    list: PmsProductEs[];
    statisticsResult: {
      stockStatus: string;
      count: number;
    }[];
  }>('/admin/pms/product/list', data);
};

export const getProductUpdateInfo = (id: number) => {
  return request.get<BaseRes<ProductUpdateInfo>>(
    `/pms/product/updateInfo/${id}`,
  );
};

export const getProductUpdateInfoNew = (id: number) => {
  return ginzaxiaomaApiRequest.get<ProductUpdateInfoNewType>(
    `/admin/pms/product/${id}`,
  );
};

export const updateProduct = (productUpdateInfo: ProductUpdateInfo) => {
  return request.post<BaseRes<{ data: number }>>(
    `/pms/product/update/${productUpdateInfo.id}`,
    productUpdateInfo,
  );
};
export const deleteProduct = (id: number) => {
  return ginzaxiaomaApiRequest.delete(`/admin/pms/product/${id}`);
};

export const createProduct = (productCreateInfo: ProductCreateInfo) => {
  return request.post<BaseRes<{ data: number }>>(
    '/pms/product/create',
    productCreateInfo,
  );
};

/** 刻印 */
export const getStampList = () => {
  return request.get<
    BaseRes<
      {
        name: string;
        itemList: ProductAttributeCode[];
      }[]
    >
  >(`/pms/productAttributeCode/list?name=stamp&categoryId=0&parentId=0`);
};

export const publishProduct = (idList: number[]) => {
  return ginzaxiaomaApiRequest.post('/search-product-es/publish-batch-auth', {
    idList,
  });
};

export const unPublishProduct = (idList: number[]) => {
  return ginzaxiaomaApiRequest.post(
    '/search-product-es/un-publish-batch-auth',
    {
      idList,
    },
  );
};

export const syncProductToEs = (idList: number[]) => {
  return ginzaxiaomaApiRequest.post('/search-product-es/update-batch', {
    idList,
  });
};

export const getProductCateList = ({
  id = 0,
  pageNum = 1,
  pageSize = 10,
}: ProductCateListPayload) => {
  return request.get<
    BaseRes<{
      pageNum: number;
      pageSize: number;
      total: number;
      list: ProductCateType[];
    }>
  >(`pms/productCategory/list/${id}`, { params: { pageNum, pageSize } });
};

export const getProductCateUpdateNavStatus = (
  params: ProductCateUpdateNavStatusPayload,
) => {
  return request.post(`pms/productCategory/update/navStatus`, null, { params });
};

export const getProductCateUpdateShowStatus = (
  params: ProductCateUpdateShowStatusPayload,
) => {
  return request.post(`pms/productCategory/update/showStatus`, null, {
    params,
  });
};

export const getProductCateOperation = (
  id: ProductCateType['id'],
  type: 'enable' | 'disable',
) => {
  return request.post(`pms/productCategory/${type}/${id}`, null);
};

export const getProductCate = (id: ProductCateType['id']) => {
  return request.get<BaseRes<ProductCateType>>(`pms/productCategory/${id}`);
};

export const getProductCateCreate = (data: ProductCateType) => {
  return request.post<BaseRes<ProductCateType>>(
    `pms/productCategory/create`,
    data,
  );
};

export const getProductCateUpdate = (data: ProductCateType) => {
  return request.post<BaseRes<ProductCateType>>(
    `pms/productCategory/update/${data.id}`,
    data,
  );
};

/** 翻译 */
export const omsTranslate = (data: any) => {
  return ginzaxiaomaApiRequest.post(`/admin/translate`, data);
};

export const getProductStockStatusHistory = (productId: number) => {
  return ginzaxiaomaApiRequest.get<
    {
      id: number;
      productId: number;
      previousStockStatus?: string;
      currentStockStatus: string;
      transactionTime: string;
      note: string;
      createdBy: string;
    }[]
  >(`/admin/pms/product-stock-status/history/${productId}`);
};

export type PmsProductActivity = {
  actId: number | string;
  actUsername?: string;
  actProductId: number;
  actProductSn: string;
  actCreateAt: string;
  actAction: string;
  actDetail?: string;
};

export const getProductActivityList = (data: {
  pageNum: number;
  pageSize: number;
  productIds?: number[];
  usernames?: string[];
  emails?: string[];
  action?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return ginzaxiaomaApiRequest.post<{
    list: PmsProductActivity[];
    total: number;
  }>('/admin/pms/product-activity/list', data);
};

export const updateStockStatus = (dto: {
  productIdList: number[];
  newStockStatus: PMS_PRODUCT_STOCK_STATUS;
  note?: string;
  autoPublish?: boolean;
}) => {
  return ginzaxiaomaApiRequest.post(
    '/admin/pms/product/update-stock-status',
    dto,
  );
};

export const getProductPriceComparison = () => {
  return ginzaxiaomaApiRequest.get<{
    mismatches: {
      dbPrice: number;
      esPrice: number;
      productId: number;
      productSn: string;
      currency: string;
    }[];
  }>('/admin/pms/product-price-comparison');
};

export const createStockTaking = (data: {
  name: string;
  stockPlaces: string[];
  note?: string;
}) => {
  return ginzaxiaomaApiRequest.post(
    '/admin/pms/product/stock-taking/dashboard',
    data,
  );
};

export enum STOCK_TAKING_RECORD_CHECK_STATUS {
  CONFIRMED = 'CONFIRMED',
  UNCONFIRMED = 'UNCONFIRMED',
}

export type PmsProductStockTakingRecord = {
  id: number;
  stockTakingId: number;
  productId: number;
  productSn: string;
  productName: string;
  productPic: string;
  stockStatus: PMS_PRODUCT_STOCK_STATUS;
  checkMan?: string;
  checkStatus: STOCK_TAKING_RECORD_CHECK_STATUS;
  stock: number;
  currency: string;
  price: number;
  sourceType: number;
  orderStatus: number;
  skuType?: number;
  checkedQuantity?: number | null;
};

export type PmsProductStockTaking = {
  id: number;
  name: string;
  stockPlace: string;
  operateMan: string;
  createTime: string;
  note?: string;
};

export const getStockTakingList = (data: {
  pageNum: number;
  pageSize: number;
  keyword?: string;
}) => {
  return ginzaxiaomaApiRequest.get<{
    total: number;
    list: PmsProductStockTaking[];
  }>('/admin/pms/product/stock-taking/list', {
    params: data,
  });
};

export const getStockTakingDetail = (stockTakingId: number) => {
  return ginzaxiaomaApiRequest.get<
    PmsProductStockTaking & {
      recordList: {
        [key: string]: PmsProductStockTakingRecord[];
      };
    }
  >(`/admin/pms/product/stock-taking/dashboard`, {
    params: {
      stockTakingId,
    },
  });
};
export const updateStockTaking = (data: {
  stockTakingId: number;
  name: string;
  note?: string;
}) => {
  return ginzaxiaomaApiRequest.put(
    `/admin/pms/product/stock-taking/dashboard`,
    data,
  );
};

export const deleteStockTaking = (stockTakingId: number) => {
  return ginzaxiaomaApiRequest.delete(
    `/admin/pms/product/stock-taking/dashboard`,
    {
      data: {
        stockTakingId,
      },
    },
  );
};

export const updateStockStatusForStockTaking = (data: {
  productIdList: number[];
  newStockTakingStatus: STOCK_TAKING_RECORD_CHECK_STATUS;
  stockTakingId: number;
  checkedQuantity?: number;
}) => {
  return ginzaxiaomaApiRequest.post(
    '/admin/pms/product/update-stock-status/stock-taking',
    data,
  );
};

export const downloadStockTakingCsv = async (stockTakingId: number) => {
  try {
    type StockTakingCsvRecord = Omit<
      PmsProductStockTakingRecord,
      'sourceType'
    > & {
      sourceType?: number;
      oldProductSn: string | null;
      brandName: string | null;
      entryTime: string | Date | null;
      stockPlace: string | null;
      remark: string | null;
    };

    const formatDateOnly = (value?: string | number | Date | null) => {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      if (value instanceof Date) {
        return value.toISOString().slice(0, 10);
      }
      if (typeof value === 'number') {
        return new Date(value).toISOString().slice(0, 10);
      }
      const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : value;
    };

    // 发起请求获取数据
    const data = await ginzaxiaomaApiRequest.get<{
      stockTaking: PmsProductStockTaking;
      stockTakingRecordList: StockTakingCsvRecord[];
    }>(`/admin/pms/product/stock-taking/csv/${stockTakingId}`);

    const { stockTaking, stockTakingRecordList } = data;
    const headers = [
      '商品ID',
      '旧商品編号',
      '品牌名',
      '商品名',
      '入库日',
      '库存地',
      '在庫状態',
      '库存类型',
      '確認状態',
      '訂單状態',
      '価格',
      '在庫数',
      '確認数量',
      '商品来源',
      '备注',
    ];
    const sourceTypeLabels = {
      [PRODUCT_SOURCE_TYPE_MAP.RECYCLE]: '買取',
      [PRODUCT_SOURCE_TYPE_MAP.CONSIGNMENT]: '委託',
      [PRODUCT_SOURCE_TYPE_MAP.PROCUREMENT]: 'その他(業者仕入)',
    };

    const { orderStatusOptions } = store.getState().globalInfo;

    const mappedData = stockTakingRecordList.map((record) => {
      const inStockDate = record.entryTime;
      const stockPlace = record.stockPlace ?? stockTaking.stockPlace;
      const stockStatusLabelKey = PMS_PRODUCT_STOCK_STATUS_OPTION_LIST.find(
        (option) => option.value === record.stockStatus,
      )?.labelKey;
      const stockTypeLabelKey = PRODUCT_STOCK_TYPE_OPTION_LIST.find(
        (option) => option.value === record.skuType,
      )?.labelKey;
      const orderStatusLabel =
        orderStatusOptions.find((o) => o.value === record.orderStatus)?.label ??
        '-';

      return {
        商品ID: record.productId,
        旧商品編号: record.oldProductSn || '-',
        品牌名: record.brandName || '-',
        商品名: record.productName,
        入库日: formatDateOnly(inStockDate),
        库存地: stockPlace || '-',
        在庫状態: stockStatusLabelKey
          ? i18n.t(stockStatusLabelKey)
          : record.stockStatus,
        库存类型: stockTypeLabelKey ? i18n.t(stockTypeLabelKey) : '-',
        確認状態:
          record.checkStatus === STOCK_TAKING_RECORD_CHECK_STATUS.CONFIRMED
            ? '確認済み'
            : '確認待ち',
        訂單状態: orderStatusLabel,
        価格: `${record.currency} ${record.price.toLocaleString('en-US')}`,
        在庫数: record.stock,
        確認数量:
          record.checkStatus === STOCK_TAKING_RECORD_CHECK_STATUS.CONFIRMED
            ? (record.checkedQuantity ?? 1)
            : '-',
        商品来源:
          record.sourceType === undefined || record.sourceType === null
            ? '-'
            : sourceTypeLabels[record.sourceType] || String(record.sourceType),
        备注: record.remark || '-',
      };
    });

    // 将数据转换为 Excel 格式
    const worksheet = XLSX.utils.json_to_sheet(mappedData, {
      header: headers,
    });

    // 创建工作簿
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Taking');

    // 生成 Excel 文件并触发下载
    XLSX.writeFile(workbook, `棚卸確認-${stockTaking.name}.xlsx`);
  } catch (error) {
    console.error('下载 Excel 文件时出错:', error);
  }
};

export const printReceiptProductName = (productIdList: number[]) => {
  return ginzaxiaomaApiRequest.post<{ [key: string]: string[] }>(
    '/admin/pms/print-receipt/product-name',
    {
      productIdList,
    },
  );
};

export const productChangeStock = (dto: {
  productId: number;
  changeStock: number;
  note: string;
}) => {
  return ginzaxiaomaApiRequest.post('/admin/pms/product/change-stock', dto);
};

const createProductDtoSchema = z.object({
  owner: z.string().optional(),
  productCategoryId: z.number(),
  boxId: z.number().nullable().optional(),
  extendType: z.string().optional().nullable(),
  stockPlace: z.string().nonempty(),
  brandName: z.string(),
  currency: z.string(),
  price: z.number(),
  costPrice: z.number(),
  careCost: z.number().nullable().optional(),
  referencePrice: z.number().nullable(),
  skuType: z.number(),
  stock: z.number(),
  sourceType: z.number(),
  promotionType: z.number(),
  originalPrice: z.number().nullable(),
  description: z.string().optional(),
  remark: z.string().optional(),
  rank: z.string(),
  colors: z.string().optional(),
  color: z.array(z.string().nonempty()).optional(),
  colorRemark: z.string().optional(),
  material: z.array(z.string().nonempty()).optional(),
  materialRemark: z.string().optional(),
  hardware: z.array(z.string().nonempty()).optional(),
  hardwareRemark: z.string().optional(),
  stamp: z.string().optional(),
  stampRemark: z.string().optional(),
  fullStamp: z.string().optional(),
  accessory: z.array(z.string().nonempty()).optional(),
  collections: z.array(z.string().nonempty()).optional(),
  facebookEnabled: z.number(),
  albumPics: z.array(z.string()).optional(),
  i18nList: z
    .array(
      z.object({
        lang: z.string(),
        name: z.string(),
        rankDesc: z.string().optional(),
        size: z.string().optional(),
        subTitle: z.string().optional(),
        note: z.string().optional(),
        detailTitle: z.string().optional(),
      }),
    )
    .nonempty(),
  priceHkd: z.number().nullable().optional(),
  // 不用 z.enum：TS 4.9 无法解析 Zod4 的 const 泛型签名
  taxRegime: z
    .custom<ProductTaxRegime>(
      (val) =>
        val === ProductTaxRegime.STANDARD_TVA ||
        val === ProductTaxRegime.MARGIN,
    )
    .nullable()
    .optional(),
  internalImages: z.array(z.string().nonempty()),
});

export type CreateProductDtoType = z.infer<typeof createProductDtoSchema>;

export const createProductNew = (data: CreateProductDtoType) => {
  const validatedData = createProductDtoSchema.parse(data);
  return ginzaxiaomaApiRequest.post<{
    id: number;
  }>('/admin/pms/product/create', validatedData);
};

export type UpdateProductDtoType = Omit<
  CreateProductDtoType,
  'stock' | 'skuType'
>;

export const updateProductNew = ({
  id,
  data,
}: {
  id: number;
  data: UpdateProductDtoType;
}) => {
  return ginzaxiaomaApiRequest.post<{
    id: number;
  }>(`/admin/pms/product/update/${id}`, data);
};

export type SoldProductBindOrderResultItem = {
  productId: number;
  success: boolean;
  orderId?: number;
  message?: string;
};

export type SoldProductBindOrderResult = {
  success: boolean;
  successCount: number;
  failCount: number;
  results: SoldProductBindOrderResultItem[];
};

export const soldProductBindOrder = (productIds: number[]) => {
  return ginzaxiaomaApiRequest.post<SoldProductBindOrderResult>(
    `/admin/pms/sold-product/bind-order`,
    {
      productIds,
    },
  );
};

export type BatchConvertToMultiStockResultItem = {
  productId: number;
  productSn?: string;
  success: boolean;
  message?: string;
};

export type BatchConvertToMultiStockResult = {
  success: boolean;
  successCount: number;
  failCount: number;
  results: BatchConvertToMultiStockResultItem[];
};

export const batchConvertToMultiStock = (dto: {
  productIdList: number[];
  stock: number;
}) => {
  return ginzaxiaomaApiRequest.post<BatchConvertToMultiStockResult>(
    '/admin/pms/product/batch-convert-to-multi-stock',
    dto,
  );
};

export type CopyProductImagesItem = {
  sourceProductId: number;
  targetProductId: number;
};

export type CopyProductImagesResult = {
  sourceProductId: number;
  targetProductId: number;
  success: boolean;
  message?: string;
  copied?: {
    albumPics: boolean;
    internalImages: boolean;
  };
};

/** 复制商品照片（商品相册 + 内部照片）到目标商品 */
export const copyProductImages = (items: CopyProductImagesItem[]) => {
  return ginzaxiaomaApiRequest.post<CopyProductImagesResult[]>(
    '/admin/pms/product/copy-images',
    { items },
  );
};

export type ProductAlbumBatchUploadFile = {
  file: File;
  relativePath: string;
};

export type ProductAlbumBatchUploadPayload = {
  productId: number;
  albumPics?: string;
  internalImages?: string;
};

export const batchUploadProductAlbumPictures = (
  items: ProductAlbumBatchUploadPayload[],
) => {
  return ginzaxiaomaApiRequest.post<void>(
    '/admin/pms/product/album/batch-upload',
    items,
  );
};

export const updateProductRemark = (id: number, remark?: string) => {
  return ginzaxiaomaApiRequest.post<{ success: boolean }>(
    `/admin/pms/product/update-remark/${id}`,
    { remark },
  );
};

export const getProductPassportUrlList = (productIds: number[]) => {
  return ginzaxiaomaApiRequest.post<ProductPassportUrlItem[]>(
    '/admin/pms/product/passport-url/list',
    { productIds },
  );
};
